// Mini chart data patterns for different metrics

// Generate a sine wave pattern
export const generateSineWaveData = (min = 20, max = 80, points = 20) => {
  const amplitude = (max - min) / 2;
  const offset = min + amplitude;
  
  return Array.from({ length: points }).map((_, i) => {
    return offset + Math.sin((i / points) * Math.PI * 2) * amplitude;
  });
};

// Generate an uptrend pattern
export const generateUptrendData = (min = 20, max = 80, points = 20) => {
  const step = (max - min) / points;
  
  return Array.from({ length: points }).map((_, i) => {
    // Add some randomness to the trend
    const randomFactor = Math.random() * 10 - 5;
    return min + (step * i) + randomFactor;
  });
};

// Generate a downtrend pattern
export const generateDowntrendData = (min = 20, max = 80, points = 20) => {
  const step = (max - min) / points;
  
  return Array.from({ length: points }).map((_, i) => {
    // Add some randomness to the trend
    const randomFactor = Math.random() * 10 - 5;
    return max - (step * i) + randomFactor;
  });
};

// Generate a stable pattern with small fluctuations
export const generateStableData = (base = 50, variance = 5, points = 20) => {
  return Array.from({ length: points }).map(() => {
    return base + (Math.random() * variance * 2) - variance;
  });
};

// Generate a volatile pattern with large fluctuations
export const generateVolatileData = (base = 50, variance = 15, points = 20) => {
  return Array.from({ length: points }).map(() => {
    return base + (Math.random() * variance * 2) - variance;
  });
};

// Generate a seasonal pattern (for crop cycles)
export const generateSeasonalData = (min = 20, max = 80, points = 20) => {
  return Array.from({ length: points }).map((_, i) => {
    // Create a pattern that rises and falls with seasons
    const seasonalFactor = Math.sin((i / (points / 4)) * Math.PI) * ((max - min) / 2);
    const base = min + (max - min) / 2;
    const randomFactor = Math.random() * 5 - 2.5;
    return base + seasonalFactor + randomFactor;
  });
};

// Generate a recovery pattern (for soil regeneration)
export const generateRecoveryData = (min = 20, max = 80, points = 20) => {
  return Array.from({ length: points }).map((_, i) => {
    // Start low, then recover with diminishing returns
    const progress = i / points;
    const recoveryFactor = 1 - Math.exp(-4 * progress);
    const value = min + (max - min) * recoveryFactor;
    const randomFactor = Math.random() * 5 - 2.5;
    return value + randomFactor;
  });
};

// Generate a biodiversity pattern (increasing with fluctuations)
export const generateBiodiversityData = (min = 20, max = 80, points = 20) => {
  return Array.from({ length: points }).map((_, i) => {
    // Biodiversity increases with fluctuations
    const progress = i / points;
    const baseValue = min + (max - min) * (Math.pow(progress, 0.7));
    const seasonalFactor = Math.sin((i / (points / 8)) * Math.PI * 2) * 5;
    return baseValue + seasonalFactor;
  });
};

// Predefined data patterns for specific metrics
export const metricPatterns = {
  temperature: generateSineWaveData(30, 70),
  water: generateStableData(60, 10),
  soil: generateStableData(40, 5),
  nutrient: generateUptrendData(20, 60),
  precipitation: generateDowntrendData(40, 80),
  yield: generateUptrendData(30, 90),
  humidity: generateDowntrendData(60, 90),
  leafTemp: generateSineWaveData(40, 60),
  leafWetness: generateStableData(80, 10),
  canopyCover: generateUptrendData(40, 80),
  biodiversity: generateBiodiversityData(30, 85),
  soilHealth: generateRecoveryData(25, 75),
  cropRotation: generateSeasonalData(30, 90),
  pestResistance: generateUptrendData(40, 85),
  carbonSequestration: generateUptrendData(20, 70),
  waterEfficiency: generateUptrendData(40, 90)
};

// DMV-specific data patterns for the mini charts
// These patterns are tailored to match the agricultural conditions in the DMV region
export const dmvMetricPatterns = {
  // DMV region temperature patterns - slightly cooler than generic
  temperature: generateSineWaveData(25, 65),
  
  // DMV region water patterns - more stable due to consistent rainfall
  water: generateStableData(65, 8),
  
  // DMV region soil patterns - richer soil with higher baseline
  soil: generateRecoveryData(50, 75),
  
  // DMV region nutrient patterns - good nutrient management practices
  nutrient: generateUptrendData(30, 70),
  
  // DMV region precipitation patterns - consistent rainfall
  precipitation: generateStableData(55, 15),
  
  // DMV region yield patterns - strong agricultural productivity
  yield: generateUptrendData(40, 85),
  
  // DMV region humidity patterns - higher humidity levels
  humidity: generateStableData(70, 10),
  
  // DMV region leaf temperature patterns - moderate leaf temperatures
  leafTemp: generateSineWaveData(45, 65),
  
  // DMV region leaf wetness patterns - higher due to humidity
  leafWetness: generateStableData(75, 10),
  
  // DMV region canopy cover patterns - good growth patterns
  canopyCover: generateUptrendData(45, 85),
  
  // DMV region biodiversity patterns - improving with sustainable practices
  biodiversity: generateBiodiversityData(45, 90),
  
  // DMV region soil health patterns - recovering from previous monoculture
  soilHealth: generateRecoveryData(35, 80),
  
  // DMV region crop rotation patterns - seasonal changes with crop rotation
  cropRotation: generateSeasonalData(40, 95),
  
  // DMV region pest resistance patterns - improving with diverse crops
  pestResistance: generateUptrendData(50, 90),
  
  // DMV region carbon sequestration - improving with sustainable practices
  carbonSequestration: generateUptrendData(30, 75),
  
  // DMV region water efficiency - improving with better irrigation
  waterEfficiency: generateUptrendData(50, 95)
}; 